import { CircularProgress, FormControlLabel, Stack, Switch } from '@mui/material';
import { AxiosError } from 'axios';
import {
  FC,
  FormEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  AuthenticationContext,
  notificationsActions,
  useAppDispatch,
  useUpdateAssociate,
} from '@vs-online-only-app/state';
import SmallButton from '../../SmallButton';

type PropertyName = 'features.browserNotifications.allowShow';
type Props = {
  propertyName: PropertyName;
  label: string;
  onFocus: () => void;
  abort: boolean;
};

const getValue = (obj: any, keyPath: string) => {
  if (keyPath.includes('.')) {
    const keys = keyPath.split('.');
    return keys.reduce((acc, key) => acc?.[key], obj);
  }
  return obj?.[keyPath];
}

const setValue = (obj: any, keyPath: string, value: any) => {
  const keys = keyPath.split('.');
  if (keys.length === 1) {
    obj[keyPath] = value;
    return obj;
  } else {
    let objCopy = obj;
    for (const key of keys.slice(0, keys.length - 1)) {
      if (objCopy[key] === undefined) {
        objCopy[key] = {};
      }
      objCopy = objCopy[key];
    }
    objCopy[keys[keys.length - 1]] = value;
  }
  return obj;
}

const EditProfileSwitch: FC<Props> = ({ propertyName, label, onFocus, abort }) => {
  const dispatch = useAppDispatch();
  const { user } = useContext(AuthenticationContext);
  const [isEditModeOn, setEditModeOn] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const updateAssociate = useUpdateAssociate();
  const [propertyValue, setPropertyValue] = useState<boolean | undefined>(false);

  useEffect(() => {
    if (!user) return;
    setPropertyValue(getValue(user, propertyName));
  }, [propertyName, user]);

  useEffect(() => {
    if (!abort || !user) return;
    setEditModeOn(false);
    setPropertyValue(getValue(user, propertyName));
    setErrorMessage('');
  }, [abort, propertyName, user]);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked;
    const isEditedValue = getValue(user, propertyName) !== value;
    onFocus?.();
    setEditModeOn(isEditedValue);
    setPropertyValue(value);
  }, [onFocus, propertyName, user]);

  const onSave = useCallback(async () => {
    const value = propertyValue;

    setIsBusy(true);
    try {
      const backendField = propertyName.replace(/^features\./, '');
      const updateFields = setValue({}, backendField, value);
      await updateAssociate.mutateAsync(updateFields);
      setEditModeOn(false);
      dispatch(
        notificationsActions.setCurrentNotification({
          message: `${label} has been updated.`,
          severity: 'success',
        }),
      );
    } catch (e) {
      if (e instanceof AxiosError) {
        const err = (e as AxiosError<{ message: string }>).response?.data
          ?.message;
        dispatch(
          notificationsActions.setCurrentNotification({
            message:
              typeof err === 'string' ? err : `Could not update ${label}`,
            severity: 'error',
          }),
        );
      } else {
        dispatch(
          notificationsActions.setCurrentNotification({
            message: (e as Error).message,
            severity: 'error',
          }),
        );
      }
    }
    setIsBusy(false);
  }, [propertyValue, propertyName, updateAssociate, dispatch, label]);

  const onCancel = useCallback(() => {
    if (!user) return;
    setPropertyValue(getValue(user, propertyName));

    setEditModeOn(false);
    setErrorMessage('');
  }, [propertyName, user]);

  const isSaveButtonDisabled = useMemo(() => {
    if (!user) return;
    return getValue(user, propertyName) === propertyValue;
  }, [user, propertyName, propertyValue]);

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSave();
  };

  return (
    <form onSubmit={handleFormSubmit} style={{ width: '100%' }}>
      <Stack direction="column" gap={2}>
        <FormControlLabel control={<Switch checked={propertyValue} onChange={handleChange} />} label={label} />
        {isEditModeOn && (
          <Stack direction="row" justifyContent="right">
            <SmallButton disabled={isBusy} onClick={onCancel}>
              Cancel
            </SmallButton>
            <SmallButton
              disabled={!!errorMessage || isSaveButtonDisabled || isBusy}
              variant="contained"
              onClick={onSave}
            >
              {isBusy ? <CircularProgress color="inherit" size={20} /> : 'Save'}
            </SmallButton>
          </Stack>
        )}
      </Stack>
    </form>
  );
};

export default EditProfileSwitch;
